// The short prompts above the chat box (#64). They change with the moment in the
// chat, all on this device: no AI reads the chat to suggest them, so a chat with a
// person stays between the two people, and replies stay in their own words.

import { looksLikeCrisis } from '../lib/crisis';

export type PromptStage = 'start' | 'afterFirst' | 'afterLong' | 'heavy' | 'quiet' | 'flow';
type Role = 'VENT' | 'LISTEN';

const PROMPTS: Record<Role, Record<PromptStage, string[]>> = {
  // For the person who chose Vent.
  VENT: {
    start: ["It's been a heavy day.", "I just need someone to listen.", "I don't know where to start.", "Can I just get this off my chest?", "I've been holding this in for a while.", "No advice needed, just listen?", "Something's been on my mind.", "I can't say this to anyone I know."],
    afterFirst: ["There's more to it…", "It's about someone close to me.", "I don't know how to explain it.", "Can I just vent for a bit?", "It's been going on for a while."],
    afterLong: ["That actually helps.", "I hadn't thought of it that way.", "I'm not sure that fits my situation.", "Thank you, really."],
    heavy: ["Thank you for listening.", "I'm okay, just needed to say it.", "Can we keep talking?"],
    quiet: ["Sorry, still here.", "Just thinking.", "Give me a sec.", "Are you still there?"],
    flow: ["That's exactly it.", "Honestly, I'm not sure.", "I feel a bit lighter now.", "It's hard to put into words.", "Thank you for listening."],
  },
  // For the person who chose Listen.
  LISTEN: {
    start: ["I'm here to listen.", "Take your time.", "Whenever you're ready.", "No judgment here.", "What's on your mind?"],
    afterFirst: ["Want to tell me more?", "I'm listening.", "How long has this been going on?", "That sounds like a lot."],
    afterLong: ["Thank you for telling me all that.", "That's a lot to carry.", "Which part feels heaviest?", "I'm glad you said it out loud."],
    heavy: ["I'm here with you.", "Are you safe right now?", "Take all the time you need.", "I'm really glad you told me."],
    quiet: ["I'm still here 🤍", "No rush.", "Still with you.", "Take your time."],
    flow: ["That makes sense.", "How are you feeling right now?", "What would help most right now?", "Thank you for trusting me with this.", "Glad you're talking about it."],
  },
};

type ChatLine = { sender: 'user' | 'other'; text?: string; timestamp?: number };

export const QUIET_MS = 45_000;
const LONG_WORDS = 25;

// Which moment the chat is in, from the real messages (no system notes).
export function promptStage(lines: ChatLine[], now: number): PromptStage {
  if (!lines.length) return 'start';
  const last = lines[lines.length - 1];
  const lastOther = [...lines].reverse().find(l => l.sender === 'other');
  if (last.timestamp && now - last.timestamp >= QUIET_MS) return 'quiet';
  if (lastOther && last === lastOther) {
    if (looksLikeCrisis(lastOther.text ?? '')) return 'heavy';
    if ((lastOther.text ?? '').trim().split(/\s+/).length >= LONG_WORDS) return 'afterLong';
    if (lines.filter(l => l.sender === 'other').length === 1) return 'afterFirst';
  }
  if (!lines.some(l => l.sender === 'user')) return 'start';
  return 'flow';
}

// Four prompts for this moment, in a fresh order.
export const promptsFor = (role: Role, stage: PromptStage, count = 4) =>
  [...PROMPTS[role][stage]].sort(() => Math.random() - 0.5).slice(0, count);

// Shown to help someone start or pick back up, and out of the way once they are
// writing freely: while typing, and after their own second message, unless it went
// quiet or the other person just shared something long or heavy.
export const showPrompts = (stage: PromptStage, myCount: number, typing: boolean) =>
  !typing && (myCount < 2 || stage === 'quiet' || stage === 'afterLong' || stage === 'heavy');
